import React, { useMemo, useState } from "react";

import {
  Button as ShadcnButton,
  buttonVariants,
  ButtonVariant as ShadcnButtonVariant,
  ButtonSize as ShadcnButtonSize,
} from "./base/button";
import { useFormContext } from "./form/form";

type ButtonVariant = "default" | "subtle" | "large";
type ButtonSize = "default" | "sm";

interface Props extends Omit<
  React.ComponentProps<typeof ShadcnButton>,
  "variant" | "size"
> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  onClick?: (
    event: React.MouseEvent<HTMLButtonElement>,
  ) => void | Promise<unknown>;
}

export function Button({
  variant,
  size,
  pending: propsPending,
  onClick,
  className,
  children,
  type = "button",
  ...props
}: Props) {
  const formContext = useFormContext();
  const [localPending, setLocalPending] = useState(false);

  const pending = useMemo(() => {
    if (propsPending || localPending) {
      return true;
    }

    if (type === "submit" && formContext?.pending) {
      return true;
    }

    return false;
  }, [propsPending, localPending, type, formContext?.pending]);

  // Map our variants to shadcn variants
  let shadcnVariant: ShadcnButtonVariant;
  let defaultSize: ShadcnButtonSize;

  if (variant === "subtle") {
    shadcnVariant = "outline";
    defaultSize = "sm";
  } else if (variant === "large") {
    shadcnVariant = "default";
    defaultSize = "lg";
  } else {
    shadcnVariant = "default";
    defaultSize = "default";
  }

  const shadcnSize: ShadcnButtonSize = size ?? defaultSize;

  return (
    <ShadcnButton
      {...props}
      type={type}
      variant={shadcnVariant}
      size={shadcnSize}
      className={className}
      pending={pending}
      onClick={(event) => {
        const returnValue = onClick?.(event);

        if (returnValue instanceof Promise) {
          setLocalPending(true);
          void returnValue.finally(() => {
            setLocalPending(false);
          });
        }
      }}
    >
      {children}
    </ShadcnButton>
  );
}

export { buttonVariants };
